import { WorkspaceAssistant } from '@/features/assistant/WorkspaceAssistant';
import { toast } from 'sonner';
import { useEffect, useState } from 'react';
import { UserRoundCheck } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';
import { Input } from '@/components/ui/input';
import { DeleteStudy } from './DeleteStudy';
import { UploadIntake } from './UploadIntake';
import { Button } from '@/components/ui/button';
import { usePregnancies } from '@/features/emr';
import { IconAction } from '@/components/ui/icon-action';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CreatePatientFromSource, usePatient, usePatients } from '@/features/emr';
import { apiError, type Study, useImagingApi, useImagingCapabilities, useImagingQuery } from './api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export function ImagingPage() {
  const { org, userId } = useImagingApi();
  return <Workspace key={`${org}:${userId}`} />;
}
export function IntakeTable({ onMatch }: { onMatch: (s: Study) => void }) {
  const [offset, setOffset] = useState(0);
  const cap = useImagingCapabilities();
  const list = useImagingQuery<Study[]>(
    `/intake?unmatched=true&offset=${offset}`,
    cap.data?.permissions.includes('intake_imaging'),
  );
  return (
    <div className="flex flex-col gap-3">
      <h2>Studies awaiting patient / pregnancy matching</h2>
      {list.isError && <p>{apiError(list.error)}</p>}
      {list.data?.map((s) => (
        <div key={s.id} className="flex flex-wrap justify-between gap-3">
          <span>
            {s.metadata.PatientName} · {s.metadata.PatientID} · {s.metadata.StudyDescription || 'Ultrasound'}
          </span>
          <div className="flex flex-wrap gap-2">
            <IconAction
              variant="outline"
              icon={UserRoundCheck}
              label="Match patient & pregnancy"
              onClick={() => onMatch(s)}
            />
            {!s.patient_id && (
              <CreatePatientFromSource
                kind="imaging"
                sourceId={s.id}
                onComplete={(result) =>
                  onMatch({ ...s, patient_id: result.patient.id, revision: result.study_revision ?? s.revision })
                }
              />
            )}
            <DeleteStudy study={s} />
          </div>
        </div>
      ))}
      {list.data?.length === 0 && <p>No studies awaiting patient / pregnancy matching.</p>}
      <div className="flex gap-2">
        <Button variant="ghost" disabled={!offset} onClick={() => setOffset(offset - 100)}>
          Previous
        </Button>
        <Button variant="ghost" disabled={(list.data?.length || 0) < 100} onClick={() => setOffset(offset + 100)}>
          Next
        </Button>
      </div>
    </div>
  );
}
export function MatchStudy({ study, close }: { study: Study; close: () => void }) {
  const [search, setSearch] = useState('');
  const [patient, setPatient] = useState(study.patient_id || '');
  const [pregnancy, setPregnancy] = useState('');
  useEffect(() => {
    setPatient(study.patient_id || '');
    setPregnancy(study.pregnancy_id || '');
  }, [study.id, study.patient_id, study.pregnancy_id]);
  const [busy, setBusy] = useState(false);
  const patients = usePatients({ search, limit: 30 });
  const linkedPatient = usePatient(study.patient_id || undefined);
  const patientOptions = [...(patients.data?.data || [])];
  if (linkedPatient.data && !patientOptions.some((p) => p.id === linkedPatient.data.id))
    patientOptions.push(linkedPatient.data);
  const pregnancies = usePregnancies(patient);
  const { org, base } = useImagingApi();
  const qc = useQueryClient();
  async function save() {
    setBusy(true);
    try {
      await clientApi.post(`${base}/studies/${study.id}/match`, {
        patient_id: patient,
        pregnancy_id: pregnancy,
        revision: study.revision,
      });
      await qc.invalidateQueries({ queryKey: ['imaging', org] });
      close();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="flex flex-col gap-4 rounded border p-4">
      <h2>Match {study.metadata.PatientName}</h2>
      <p>
        Source ID: {study.metadata.PatientID} · issuer: {study.metadata.IssuerOfPatientID || 'not provided'} · DOB:{' '}
        {study.metadata.PatientBirthDate || 'not provided'}
      </p>
      <FieldGroup>
        <Field>
          <FieldLabel>Find patient</FieldLabel>
          <Input value={search} aria-label="Find patient" onChange={(e) => setSearch(e.target.value)} />
          <Select
            value={patient}
            onValueChange={(v) => {
              setPatient(v);
              setPregnancy('');
            }}>
            <SelectTrigger>
              <SelectValue placeholder="Choose patient" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {patientOptions.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.metadata_?.first_name} {p.metadata_?.last_name} · {p.medical_record_number} ·{' '}
                    {p.metadata_?.date_of_birth}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </Field>
        <Field>
          <FieldLabel>Pregnancy</FieldLabel>
          <Select value={pregnancy} onValueChange={setPregnancy}>
            <SelectTrigger>
              <SelectValue placeholder="Choose pregnancy" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {pregnancies.data?.data.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.status} · EDD {p.edd || 'unknown'} · {p.id.slice(0, 8)}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </Field>
      </FieldGroup>
      <p>
        Confirm source patient identifiers against the chart before matching. Create a pregnancy in the chart first if
        none exists.
      </p>
      {patient && (
        <Link to="/patients/$patientId" params={{ patientId: patient }}>
          Open patient chart to add or review a pregnancy
        </Link>
      )}
      <div className="flex gap-2">
        <Button disabled={!patient || !pregnancy || busy} onClick={() => void save()}>
          Confirm match
        </Button>
        <Button variant="outline" onClick={close}>
          Cancel
        </Button>
      </div>
    </section>
  );
}
function StudyTable({
  search,
  studies,
  setSearch,
}: {
  studies: Study[];
  search: string;
  setSearch: (s: string) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <Input
        value={search}
        aria-label="Search this page"
        placeholder="Search patient or study on this page"
        onChange={(e) => setSearch(e.target.value)}
      />
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Patient / source ID</TableHead>
            <TableHead>Study</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Action</TableHead>
            <TableHead className="text-right">
              <span className="sr-only">Delete study</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {studies
            .filter((s) => JSON.stringify(s.metadata).toLowerCase().includes(search.toLowerCase()))
            .map((s) => (
              <TableRow key={s.id}>
                <TableCell>
                  {s.metadata.PatientName}
                  <br />
                  {s.metadata.PatientID}
                </TableCell>
                <TableCell>
                  {s.metadata.StudyDescription || 'Ultrasound'}
                  <br />
                  {s.metadata.StudyDate}
                </TableCell>
                <TableCell>{s.status}</TableCell>
                <TableCell>
                  <Button variant="outline" asChild>
                    <Link to="/imaging/$studyId" params={{ studyId: s.id }}>
                      Open study
                    </Link>
                  </Button>
                </TableCell>
                <TableCell className="text-right">
                  <DeleteStudy study={s} />
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
      {!studies.length && <p>No matched imaging studies.</p>}
    </div>
  );
}
function Workspace() {
  const cap = useImagingCapabilities();
  const permissions = cap.data?.permissions || [];
  const intake = permissions.includes('intake_imaging');
  const read = permissions.some((p) => ['prepare_imaging', 'read_imaging'].includes(p));
  const [tab, setTab] = useState('reading');
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const list = useImagingQuery<Study[]>(
    read ? `/studies?offset=${offset}` : `/intake?offset=${offset}`,
    read || intake,
  );
  const [matching, setMatching] = useState<Study | null>(null);
  if (cap.isLoading) return <p>Loading imaging access…</p>;
  if (!intake && !read)
    return (
      <Alert>
        <AlertDescription>
          Explicit imaging access is required. Ask your organization administrator to assign an imaging role.
        </AlertDescription>
      </Alert>
    );
  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex justify-end">
        <WorkspaceAssistant
          title="Imaging assistant"
          commands={[
            ...(read ? [{ label: 'Open reading list', run: () => setTab('reading') }] : []),
            ...(intake ? [{ label: 'Show unmatched studies', run: () => setTab('intake') }] : []),
          ]}
        />
      </div>
      <Tabs value={!read ? 'intake' : tab} onValueChange={setTab}>
        <TabsList>
          {read && <TabsTrigger value="reading">Reading list</TabsTrigger>}
          {intake && <TabsTrigger value="intake">Imaging intake</TabsTrigger>}
        </TabsList>
        {read && (
          <TabsContent value="reading">
            <StudyTable search={search} setSearch={setSearch} studies={list.data || []} />
            {/*
              Inside the tab, not beside it. This paginates the reading list, but
              it used to sit outside <Tabs>, so it also rendered under the intake
              tab -- which has its own paginator in IntakeTable. Two sets of
              Previous/Next, one of them driving a list that was not on screen.
            */}
            <div className="mt-4 flex gap-2">
              <Button variant="outline" disabled={!offset} onClick={() => setOffset(Math.max(0, offset - 100))}>
                Previous
              </Button>
              <Button
                variant="outline"
                disabled={(list.data?.length || 0) < 100}
                onClick={() => setOffset(offset + 100)}>
                Next
              </Button>
            </div>
          </TabsContent>
        )}
        {intake && (
          <TabsContent value="intake">
            <div className="flex flex-col gap-6">
              <UploadIntake />
              <IntakeTable onMatch={setMatching} />
            </div>
          </TabsContent>
        )}
      </Tabs>
      {list.isError && (
        <Alert>
          <AlertDescription>{apiError(list.error)}</AlertDescription>
        </Alert>
      )}
      {matching && <MatchStudy key={matching.id} study={matching} close={() => setMatching(null)} />}
    </div>
  );
}
